import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SavedReport, ReportRequest, ChartTypeEnum, ReportModuleEnum, DatePresetEnum } from '@grudder/types/report.types';
import { AUTH_GET, AUTH_POST, AUTH_PUT, AUTH_DELETE } from '@grudder/app/actions-client';
import { config } from '@grudder/lib/config';

// ---------------------- Helper Mapping ----------------------
interface UserSettingItem {
  id?: string; _id?: string; value?: string; settingsType?: string; attributes?: any; createdAt?: string; updatedAt?: string; createdBy?: string;
}

const mapSettingToSavedReport = (setting: UserSettingItem): SavedReport | null => {
  if (!setting || setting.settingsType !== 'CUSTOM_REPORT') return null;
  const data = setting.attributes?.data || {};
  const rawIsPinned = data?.isPinned;
  const isPinned = typeof rawIsPinned === 'boolean'
    ? rawIsPinned
    : rawIsPinned === 'true' || rawIsPinned === 1 || rawIsPinned === '1';

  return {
    id: setting.id || setting._id || Math.random().toString(36).slice(2),
    name: data.name || setting.value || 'Custom Report',
    description: data.description || '',
    datePreset: (data.datePreset as DatePresetEnum) || undefined,
    xAxis: (typeof data.xAxis === 'string' ? data.xAxis : data.xAxis?.value) || '',
    yAxis: (typeof data.yAxis === 'string' ? data.yAxis : data.yAxis?.value) || '',
    chartType: data.chartType || ChartTypeEnum.BAR,
    createdAt: setting.createdAt || new Date().toISOString(),
    updatedAt: setting.updatedAt || new Date().toISOString(),
    createdBy: setting.createdBy || 'current-user',
    isTemplate: false,
    module: data.module || ReportModuleEnum.LEAD,
    metric: data.metric || 'count',
    isPinned,
    organization: data.organization || undefined,
  } as SavedReport;
};

// ---------------------- Slice State ----------------------
interface ReportsState {
  savedReports: SavedReport[];
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  error: string | null;
  lastCreatedId?: string | null;
  isFetched: boolean;
}

const initialState: ReportsState = {
  savedReports: [],
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  error: null,
  lastCreatedId: null,
  isFetched: false,
};

// ---------------------- Thunks ----------------------

export const fetchSavedReports = createAsyncThunk('reports/fetchSaved', async (_, { rejectWithValue }) => {
  try {
    const response = await AUTH_GET(`${config.baseURL}/api/users/user-settings`);
    const settingsArray: UserSettingItem[] = response?.userSettings || response || [];
    const reports = settingsArray
      .filter(s => s.settingsType === 'CUSTOM_REPORT')
      .map(s => mapSettingToSavedReport(s))
      .filter(Boolean) as SavedReport[];
    return reports;
  } catch (e: any) {
    return rejectWithValue(e?.message || 'Failed to fetch reports');
  }
});

export const createCustomReport = createAsyncThunk(
  'reports/create',
  async (payload: { config: ReportRequest & { reportName: string; description?: string } }, { rejectWithValue }) => {
    try {
      const { config: reportConfig } = payload;
      if (!reportConfig.organization) {
        throw new Error('Organization context is required to create a report');
      }
      const buildAxis = (axisValue?: string) => axisValue ? ({ value: axisValue, isCustomField: /^[0-9a-fA-F]{24}$/.test(axisValue) }) : undefined;
      const dataPayload: Record<string, any> = {
        module: reportConfig.module || ReportModuleEnum.LEAD,
        metric: reportConfig.metric || 'count',
        chartType: reportConfig.chartType || ChartTypeEnum.BAR,
        name: reportConfig.reportName,
        description: reportConfig.description || '',
        datePreset: reportConfig.datePreset || null,
        startDate: reportConfig.startDate || null,
        endDate: reportConfig.endDate || null,
        isPinned: reportConfig.isPinned ?? false,
        organization: reportConfig.organization,
      };
      const xAxisPayload = buildAxis(reportConfig.xAxis);
      const yAxisPayload = buildAxis(reportConfig.yAxis);
      if (xAxisPayload) dataPayload.xAxis = xAxisPayload;
      if (yAxisPayload) dataPayload.yAxis = yAxisPayload;

      const body = {
        settingsType: 'CUSTOM_REPORT',
        value: reportConfig.reportName,
        attributes: { data: dataPayload }
      };

      const response = await AUTH_POST(`${config.baseURL}/api/users/user-settings`, body);

      // Response shapes we may encounter:
      // 1. Single created setting object
      // 2. { userSettings: [...] } full list
      // 3. { userSetting: {...} } single (less likely)
      let mapped: SavedReport | null = null;
      if (Array.isArray((response as any)?.userSettings)) {
        // Attempt to find the newest custom report
        const customReports = (response as any).userSettings.filter((s: any) => s?.settingsType === 'CUSTOM_REPORT');
        if (customReports.length) {
          // Pick the one with max createdAt
          const newest = customReports.reduce((a: any, b: any) => (new Date(a.createdAt || 0) > new Date(b.createdAt || 0) ? a : b));
          mapped = mapSettingToSavedReport(newest);
        }
      } else if ((response as any)?.userSetting) {
        mapped = mapSettingToSavedReport((response as any).userSetting);
      } else {
        mapped = mapSettingToSavedReport(response as any);
      }

      return mapped || null;
    } catch (e: any) {
      return rejectWithValue(e?.message || 'Failed to create report');
    }
  }
);

export const updateCustomReport = createAsyncThunk(
  'reports/update',
  async (
    payload: { id: string; updates: Partial<ReportRequest & { reportName: string; description?: string }> },
    { rejectWithValue, getState }
  ) => {
    try {
      const { id, updates } = payload;
      const state = getState() as { reports?: ReportsState };
      const existing = state.reports?.savedReports?.find((r) => r.id === id);
      const buildAxis = (axisValue?: string) =>
        axisValue ? { value: axisValue, isCustomField: /^[0-9a-fA-F]{24}$/.test(axisValue) } : undefined;

      const resolvedModule = updates.module ?? existing?.module ?? ReportModuleEnum.LEAD;
      const resolvedMetric = updates.metric ?? existing?.metric ?? 'count';
      const resolvedChartType = updates.chartType ?? existing?.chartType ?? ChartTypeEnum.BAR;
      const resolvedName = updates.reportName ?? existing?.name ?? updates.reportName ?? 'Custom Report';
      const resolvedDescription = updates.description ?? existing?.description ?? '';
      const resolvedDatePreset =
        updates.datePreset !== undefined ? updates.datePreset : existing?.datePreset ?? null;
      const resolvedStartDate = updates.startDate ?? null;
      const resolvedEndDate = updates.endDate ?? null;
      const resolvedXAxis = updates.xAxis ?? existing?.xAxis ?? '';
      const resolvedYAxis = updates.yAxis ?? existing?.yAxis ?? '';
      const resolvedIsPinned = updates.isPinned ?? existing?.isPinned ?? false;
      const resolvedOrganization = updates.organization ?? existing?.organization ?? null;
      if (!resolvedOrganization) {
        throw new Error('Organization context is required to update a report');
      }

      const dataPayload: Record<string, any> = {
        module: resolvedModule,
        metric: resolvedMetric,
        chartType: resolvedChartType,
        name: resolvedName,
        description: resolvedDescription,
        datePreset: resolvedDatePreset,
        startDate: resolvedStartDate,
        endDate: resolvedEndDate,
        isPinned: resolvedIsPinned,
        organization: resolvedOrganization,
      };

      if (resolvedXAxis) dataPayload.xAxis = buildAxis(resolvedXAxis);
      if (resolvedYAxis) dataPayload.yAxis = buildAxis(resolvedYAxis);

      const body = {
        settingsType: 'CUSTOM_REPORT',
        value: resolvedName,
        attributes: { data: dataPayload }
      };
      const response = await AUTH_PUT(`${config.baseURL}/api/users/user-settings/${id}`, body);
      let mapped = mapSettingToSavedReport(response as any);
      if (!mapped && (response as any)?.userSetting) {
        mapped = mapSettingToSavedReport((response as any).userSetting);
      }
      if (!mapped && Array.isArray((response as any)?.userSettings)) {
        const customReports = (response as any).userSettings.filter((s: any) => s?.settingsType === 'CUSTOM_REPORT');
        if (customReports.length) {
          const newest = customReports.reduce((a: any, b: any) =>
            new Date(a.updatedAt || a.createdAt || 0) > new Date(b.updatedAt || b.createdAt || 0) ? a : b
          );
          mapped = mapSettingToSavedReport(newest);
        }
      }
      if (!mapped && existing) {
        mapped = {
          ...existing,
          name: resolvedName,
          description: resolvedDescription,
          datePreset: resolvedDatePreset === null ? undefined : resolvedDatePreset,
          xAxis: resolvedXAxis || existing.xAxis,
          yAxis: resolvedYAxis || existing.yAxis,
          chartType: resolvedChartType,
          module: resolvedModule,
          metric: resolvedMetric,
          updatedAt: new Date().toISOString(),
          isPinned: resolvedIsPinned,
          organization: resolvedOrganization,
        } as SavedReport;
      }
      if (!mapped) {
        mapped = {
          id,
          name: resolvedName,
          description: resolvedDescription,
          datePreset: resolvedDatePreset === null ? undefined : resolvedDatePreset,
          xAxis: resolvedXAxis,
          yAxis: resolvedYAxis,
          chartType: resolvedChartType,
          createdAt: existing?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: existing?.createdBy || 'current-user',
          isTemplate: false,
          module: resolvedModule,
          metric: resolvedMetric,
          isPinned: resolvedIsPinned,
          organization: resolvedOrganization,
        } as SavedReport;
      }
      return mapped;
    } catch (e: any) {
      return rejectWithValue(e?.message || 'Failed to update report');
    }
  }
);

export const deleteCustomReport = createAsyncThunk(
  'reports/delete',
  async (payload: { id: string }, { rejectWithValue }) => {
    try {
      const { id } = payload;
      await AUTH_DELETE(`${config.baseURL}/api/users/user-settings/${id}`);
      return id;
    } catch (e: any) {
      return rejectWithValue(e?.message || 'Failed to delete report');
    }
  }
);

// ---------------------- Slice ----------------------
const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    clearReportsState: () => initialState,
    setReports: (state, action: PayloadAction<SavedReport[]>) => {
      state.savedReports = action.payload;
    },
    invalidateReports: (state) => { state.isFetched = false; }
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchSavedReports.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSavedReports.fulfilled, (state, action) => { state.loading = false; state.savedReports = action.payload; state.isFetched = true; })
      .addCase(fetchSavedReports.rejected, (state, action: any) => { state.loading = false; state.error = action.payload; })
      // Create
      .addCase(createCustomReport.pending, (state) => { state.creating = true; state.error = null; })
      .addCase(createCustomReport.fulfilled, (state, action) => { state.creating = false; if (action.payload) { state.savedReports.push(action.payload); state.lastCreatedId = action.payload.id; } })
      .addCase(createCustomReport.rejected, (state, action: any) => { state.creating = false; state.error = action.payload; })
      // Update
      .addCase(updateCustomReport.pending, (state) => { state.updating = true; state.error = null; })
  .addCase(updateCustomReport.fulfilled, (state, action) => { state.updating = false; state.savedReports = state.savedReports.map(r => r.id === action.payload.id ? action.payload : r); state.isFetched = true; })
      .addCase(updateCustomReport.rejected, (state, action: any) => { state.updating = false; state.error = action.payload; })
      // Delete
      .addCase(deleteCustomReport.pending, (state) => { state.deleting = true; state.error = null; })
  .addCase(deleteCustomReport.fulfilled, (state, action) => { state.deleting = false; state.savedReports = state.savedReports.filter(r => r.id !== action.payload); state.isFetched = true; })
      .addCase(deleteCustomReport.rejected, (state, action: any) => { state.deleting = false; state.error = action.payload; });
  }
});

export const { clearReportsState, setReports, invalidateReports } = reportsSlice.actions;
export default reportsSlice.reducer;
